import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Paper, Popper, Stack, Typography } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { DateTime } from 'luxon';
import { defineMessages, useIntl } from 'react-intl';

import {
  BatchFindCacheUpdate,
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_FIND_GROUPS_QUERY,
  GET_MY_SITES_QUERY,
  UPDATE_FINDS_MUTATION,
  updateBatchFindsInCache,
} from '../../gql';
import { useVocabulary } from '../../i18n';
import {
  Button,
  DateTimeField,
  FieldLabel,
  FormField,
  MaterialAutocomplete,
  SurfaceAutocomplete,
  SurfaceTextField,
} from '../design-system';
import type { FindBatchUpdateAction } from './FindBatchActionsMenu';

interface FindBatchActionPopperProps {
  action: FindBatchUpdateAction;
  anchorEl: HTMLElement | null;
  findIds: readonly string[];
  onClose: () => void;
}

interface Option {
  id: string;
  name?: string;
  category?: string;
  label?: string;
  parentLabel?: string;
}

const paperStyles = {
  width: { xs: 320, sm: 430 },
  maxWidth: 'calc(100vw - 32px)',
  mt: 0.75,
  border: '1px solid',
  borderColor: 'divider',
  backgroundImage: 'none',
};
const contentStyles = { p: 2.5 };
const inputStyles = { width: '100%', p: 0 };
const noteStyles = { color: 'text.secondary' };
const footerStyles = {
  px: 2.5,
  py: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider',
};

const fieldMessages = defineMessages({
  material: {
    id: 'finds.batch.field.material',
    defaultMessage: 'material',
  },
  type: {
    id: 'finds.batch.field.type',
    defaultMessage: 'find type',
  },
  period: {
    id: 'finds.batch.field.period',
    defaultMessage: 'period',
  },
  date: {
    id: 'finds.batch.field.date',
    defaultMessage: 'find date',
  },
  tags: { id: 'finds.batch.field.tags', defaultMessage: 'tags' },
  group: { id: 'finds.batch.field.group', defaultMessage: 'group' },
  site: { id: 'finds.batch.field.site', defaultMessage: 'site' },
});
const inputFieldMessages = defineMessages({
  material: { id: 'find.field.material', defaultMessage: 'Material' },
  type: { id: 'find.field.type', defaultMessage: 'Find type' },
  period: { id: 'find.field.era', defaultMessage: 'Era' },
  date: { id: 'find.field.dateFound', defaultMessage: 'Date found' },
  tags: { id: 'find.field.tags', defaultMessage: 'Tags' },
  group: { id: 'find.field.groups', defaultMessage: 'Groups' },
  site: { id: 'form.site', defaultMessage: 'Site' },
});

const initialDrafts: Record<FindBatchUpdateAction, BatchFindCacheUpdate> = {
  material: { material: null },
  type: { type: null },
  period: { era: null },
  date: { foundAt: null },
  tags: { tags: [] },
  group: { groupIds: null },
  site: { siteId: null },
};

export function FindBatchActionPopper({
  action,
  anchorEl,
  findIds,
  onClose,
}: FindBatchActionPopperProps) {
  const intl = useIntl();
  const vocabulary = useVocabulary();
  const [draft, setDraft] = useState<BatchFindCacheUpdate>(
    initialDrafts[action]
  );
  const [dirty, setDirty] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { data: filterData, loading: filtersLoading } = useQuery(
    GET_ALL_FILTER_TYPES_QUERY,
    {
      fetchPolicy: 'cache-first',
      skip: action === 'date' || action === 'group' || action === 'site',
    }
  );
  const { data: sitesData, loading: sitesLoading } = useQuery(
    GET_MY_SITES_QUERY,
    {
      fetchPolicy: 'cache-first',
      skip: action !== 'site',
    }
  );
  const { data: groupsData, loading: groupsLoading } = useQuery(
    GET_MY_FIND_GROUPS_QUERY,
    {
      fetchPolicy: 'cache-first',
      skip: action !== 'group',
    }
  );
  const [updateFinds, { loading, error }] = useMutation(UPDATE_FINDS_MUTATION, {
    update: (cache, { data }) => {
      const updatedIds = data?.updateFinds?.updatedIds;

      if (updatedIds?.length) {
        updateBatchFindsInCache(cache, updatedIds, draft);
      }
    },
  });

  const materials = useMemo(
    () => filterData?.getAllFilterTypes?.materials || [],
    [filterData?.getAllFilterTypes?.materials]
  );
  const types = useMemo(() => filterData?.getAllFilterTypes?.findTypes || [], [
    filterData?.getAllFilterTypes?.findTypes,
  ]);
  const eras = useMemo(
    () =>
      (filterData?.getAllFilterTypes?.eras || [])
        .filter((era) => era.parentId)
        .concat([
          {
            id: 'indeterminate dating',
            parentId: 'indeterminate dating',
            ordinal: 26,
          },
        ])
        .map((era) => ({
          ...era,
          id: era.id.toLocaleLowerCase(),
          label: vocabulary('era', era.id),
          parentLabel: vocabulary('era', era.parentId),
        })),
    [filterData?.getAllFilterTypes?.eras, vocabulary]
  );
  const tags = useMemo(
    () => (filterData?.getAllFilterTypes?.tags || []).map((tag) => tag.name),
    [filterData?.getAllFilterTypes?.tags]
  );
  const sites = useMemo(
    () =>
      (sitesData?.getMySites || []).filter(
        (site) => site.canAddFinds !== false
      ),
    [sitesData?.getMySites]
  );
  const groups = useMemo(
    () =>
      (groupsData?.getMyFindGroups || []).filter(
        (group) => !group.shared || (group.level ?? 0) >= 1
      ),
    [groupsData?.getMyFindGroups]
  );
  const fieldLabel = intl.formatMessage(fieldMessages[action]);
  const inputFieldLabel = intl.formatMessage(inputFieldMessages[action]);
  const title = intl.formatMessage(
    {
      id: 'finds.batch.updateTitle',
      defaultMessage:
        'Apply update {field} on {count, plural, one {# find} other {# finds}}',
    },
    { field: fieldLabel, count: findIds.length }
  );
  const loadingOptions =
    filtersLoading ||
    (action === 'site' && sitesLoading) ||
    (action === 'group' && groupsLoading);
  const saveDisabled =
    loading ||
    loadingOptions ||
    !dirty ||
    (action === 'date' && !draft.foundAt) ||
    (action === 'tags' && !draft.tags?.length);

  const setPatch = useCallback((patch: BatchFindCacheUpdate) => {
    setDraft(patch);
    setDirty(true);
    setValidationError(null);
  }, []);
  const handleMaterialChange = useCallback(
    (_event: React.SyntheticEvent, value: string | null) =>
      setPatch({ material: value }),
    [setPatch]
  );
  const handleTypeChange = useCallback(
    (_event: React.SyntheticEvent, value: Option | null) =>
      setPatch({ type: value?.id ?? null }),
    [setPatch]
  );
  const handleEraChange = useCallback(
    (_event: React.SyntheticEvent, value: Option | null) =>
      setPatch({ era: value?.id ?? null }),
    [setPatch]
  );
  const handleDateChange = useCallback(
    (value: DateTime | null) =>
      setPatch({ foundAt: value?.isValid ? value.toISO() : null }),
    [setPatch]
  );
  const handleTagsChange = useCallback(
    (_event: React.SyntheticEvent, value: string[]) =>
      setPatch({ tags: value }),
    [setPatch]
  );
  const handleGroupsChange = useCallback(
    (_event: React.SyntheticEvent, value: Option[]) =>
      setPatch({
        groupIds: value.length ? value.map((group) => group.id) : null,
      }),
    [setPatch]
  );
  const handleSiteChange = useCallback(
    (_event: React.SyntheticEvent, value: Option | null) =>
      setPatch({ siteId: value?.id ?? null }),
    [setPatch]
  );
  const handleSave = useCallback(async () => {
    setValidationError(null);

    try {
      const result = await updateFinds({
        variables: { ids: findIds, find: draft },
      });
      const mutationResult = result.data?.updateFinds;

      if (mutationResult?.errors?.length) {
        setValidationError(mutationResult.errors[0].error);
        return;
      }

      onClose();
    } catch {
      // Apollo exposes the network error below and keeps the popper open.
    }
  }, [draft, findIds, onClose, updateFinds]);

  const renderTextInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <SurfaceTextField {...params} surface="sunken" sx={inputStyles} />
    ),
    []
  );

  let input: React.ReactNode;

  if (action === 'material') {
    input = (
      <FormField clampLabel>
        <FieldLabel htmlFor="batch-find-material">{inputFieldLabel}</FieldLabel>
        <MaterialAutocomplete
          disablePortal
          id="batch-find-material"
          getOptionLabel={(material) => vocabulary('material', material)}
          onChange={handleMaterialChange}
          options={materials}
          sx={inputStyles}
          value={draft.material ?? null}
        />
      </FormField>
    );
  } else if (action === 'type') {
    input = (
      <FormField clampLabel>
        <FieldLabel htmlFor="batch-find-type">{inputFieldLabel}</FieldLabel>
        <SurfaceAutocomplete
          disablePortal
          id="batch-find-type"
          options={types}
          groupBy={(option: Option) => vocabulary('findType', option.category)}
          getOptionLabel={(option: Option) => vocabulary('findType', option.id)}
          onChange={handleTypeChange}
          renderInput={renderTextInput}
          surface="sunken"
          value={types.find((option) => option.id === draft.type) ?? null}
        />
      </FormField>
    );
  } else if (action === 'period') {
    input = (
      <FormField clampLabel>
        <FieldLabel htmlFor="batch-find-period">{inputFieldLabel}</FieldLabel>
        <SurfaceAutocomplete
          disablePortal
          id="batch-find-period"
          options={eras}
          groupBy={(option: Option) => option.parentLabel || ''}
          getOptionLabel={(option: Option) => option.label || option.id}
          onChange={handleEraChange}
          renderInput={renderTextInput}
          surface="sunken"
          value={eras.find((option) => option.id === draft.era) ?? null}
        />
      </FormField>
    );
  } else if (action === 'date') {
    const dateValue = draft.foundAt ? DateTime.fromISO(draft.foundAt) : null;

    input = (
      <DateTimeField
        clampLabel
        id="batch-find-date"
        onChange={handleDateChange}
        required
        value={dateValue}
      />
    );
  } else if (action === 'tags') {
    input = (
      <FormField clampLabel>
        <FieldLabel htmlFor="batch-find-tags">{inputFieldLabel}</FieldLabel>
        <SurfaceAutocomplete
          freeSolo
          multiple
          disablePortal
          id="batch-find-tags"
          options={tags}
          onChange={handleTagsChange}
          renderInput={renderTextInput}
          surface="sunken"
          value={draft.tags || []}
        />
      </FormField>
    );
  } else if (action === 'group') {
    const selectedGroups = groups.filter((group) =>
      draft.groupIds?.includes(group.id)
    );

    input = (
      <FormField clampLabel>
        <FieldLabel htmlFor="batch-find-group">{inputFieldLabel}</FieldLabel>
        <SurfaceAutocomplete
          multiple
          disablePortal
          id="batch-find-group"
          options={groups}
          getOptionLabel={(option: Option) => option.name || option.id}
          isOptionEqualToValue={(option: Option, value: Option) =>
            option.id === value.id
          }
          onChange={handleGroupsChange}
          renderInput={renderTextInput}
          surface="sunken"
          value={selectedGroups}
        />
      </FormField>
    );
  } else {
    input = (
      <FormField clampLabel>
        <FieldLabel htmlFor="batch-find-site">{inputFieldLabel}</FieldLabel>
        <SurfaceAutocomplete
          disablePortal
          id="batch-find-site"
          options={sites}
          getOptionLabel={(option: Option) => option.name || option.id}
          isOptionEqualToValue={(option: Option, value: Option) =>
            option.id === value.id
          }
          onChange={handleSiteChange}
          renderInput={renderTextInput}
          surface="sunken"
          value={sites.find((site) => site.id === draft.siteId) ?? null}
        />
      </FormField>
    );
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
    >
      <Paper elevation={8} sx={paperStyles}>
        <Stack gap={2.5} sx={contentStyles}>
          <Box>
            <Typography fontWeight={600}>{title}</Typography>
            <Typography variant="body2" sx={noteStyles} mt={1}>
              {intl.formatMessage({
                id: 'finds.batch.permissionNote',
                defaultMessage:
                  'Only finds you have access level for editing to will be updated. Others will be skipped.',
              })}
            </Typography>
          </Box>
          {loadingOptions ? (
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'loading',
                defaultMessage: 'Loading…',
              })}
            </Typography>
          ) : (
            input
          )}
          {(validationError || error) && (
            <Alert severity="error" variant="outlined">
              {validationError ||
                intl.formatMessage({
                  id: 'finds.batch.saveError',
                  defaultMessage: 'The finds could not be updated.',
                })}
            </Alert>
          )}
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={1}
          sx={footerStyles}
        >
          <Button tone="neutral" disabled={loading} onClick={onClose}>
            {intl.formatMessage({
              id: 'general.cancel',
              defaultMessage: 'Cancel',
            })}
          </Button>
          <Button disabled={saveDisabled} onClick={handleSave}>
            {intl.formatMessage({ id: 'action.save', defaultMessage: 'Save' })}
          </Button>
        </Stack>
      </Paper>
    </Popper>
  );
}
